import { el, clear } from "./dom.js";
import {
	listNotifications,
	markAllRead,
	clearAll,
} from "../db/notifications.js";
import { navigate } from "../main.js";
import { formatDate } from "../utils/dates.js";

const PLACEHOLDER = "/placeholder-cover.svg";

// The bell's dropdown panel. Created once and kept in the DOM (hidden); the bell
// button toggles it. Opening it re-renders the list and marks everything read
// (clearing the badge). Returns { el, toggle, close } for main.js to wire up.
export function notificationsPanel() {
	const listWrap = el("div.notif-list", {});

	// "Clear all" only hides notices from the bell — they stay in History →
	// Notifications (design_documents/history.md decision 6).
	const clearBtn = el(
		"button.notif-clear",
		{ type: "button", title: "Hide from the bell (kept in History)", onclick: async () => { await clearAll(); render(); } },
		"Clear all",
	);
	const historyBtn = el(
		"button.notif-clear",
		{
			type: "button",
			onclick: () => {
				close();
				navigate("/history?tab=notifications");
			},
		},
		"History ›",
	);

	const panel = el(
		"div.notif-panel",
		{ hidden: true, role: "dialog", "aria-label": "Notifications" },
		el(
			"div.notif-panel__head",
			{},
			el("span.notif-panel__title", {}, "Notifications"),
			el("span", { style: { display: "flex", gap: "6px" } }, historyBtn, clearBtn),
		),
		listWrap,
	);

	let open = false;

	async function render() {
		const items = await listNotifications();
		clear(listWrap);
		clearBtn.hidden = items.length === 0;
		if (items.length === 0) {
			listWrap.append(
				el("div.notif-empty", {}, "No notifications yet."),
			);
			return;
		}
		for (const n of items) {
			listWrap.append(notifRow(n));
		}
	}

	function notifRow(n) {
		const cover = el("img.notif-item__cover", {
			src: n.cover_url || PLACEHOLDER,
			alt: "",
			onerror: (e) => {
				e.currentTarget.src = PLACEHOLDER;
			},
		});
		const row = el(
			`div.notif-item${n.read ? ".notif-item--read" : ""}`,
			{
				role: "button",
				onclick: () => {
					close();
					if (n.route) navigate(n.route);
				},
			},
			cover,
			el(
				"div.notif-item__body-wrap",
				{},
				el("div.notif-item__title", {}, n.title ?? ""),
				el("div.notif-item__body", {}, n.body ?? ""),
				el("div.notif-item__time", {}, formatDate(n.created_at)),
			),
		);
		return row;
	}

	function onDocClick(e) {
		// Clicks on the bell button are handled by its own toggle; clicks inside
		// the panel shouldn't close it. Anything else dismisses.
		if (
			e.target.closest(".notif-panel") ||
			e.target.closest('[data-action="notifications"]')
		) {
			return;
		}
		close();
	}

	function onKey(e) {
		if (e.key === "Escape") close();
	}

	function close() {
		if (!open) return;
		open = false;
		panel.hidden = true;
		document.removeEventListener("click", onDocClick, true);
		document.removeEventListener("keydown", onKey);
	}

	async function toggle() {
		if (open) {
			close();
			return;
		}
		await render();
		panel.hidden = false;
		open = true;
		// Opening clears the unread badge.
		markAllRead();
		// Defer attaching the outside-click listener so the click that opened the
		// panel doesn't immediately close it.
		setTimeout(() => {
			document.addEventListener("click", onDocClick, true);
			document.addEventListener("keydown", onKey);
		}, 0);
	}

	return { el: panel, toggle, close };
}
